import type { ApiRentalListingFull } from 'src/types/rental';

import { formatAmount } from './money';

/**
 * Seats and the daily kilometre allowance of a rental car in the words a renter reads (api#397):
 * one line on the rental card after the deposit and the city — "5 seats · 300 km/day",
 * "5 seats · Unlimited km" — and two labelled rows on the listing.
 *
 * Each part is there only when the dealer stated it: a listing published before the fields, or
 * served by an api that predates them, says nothing, and no placeholder stands in. "Unlimited" is
 * the dealer's explicit flag; a figure of zero is not an answer either.
 */
export type RentalCarTerms = Pick<ApiRentalListingFull, 'seats' | 'dailyKmLimit' | 'dailyKmUnlimited'>;

/** "5 seats", "1 seat"; nothing when the dealer did not say. */
export const seatsLabel = (seats: number | null | undefined): string | undefined =>
  typeof seats === 'number' && seats > 0 ? `${seats} ${seats === 1 ? 'seat' : 'seats'}` : undefined;

/** "300 km/day", "Unlimited km"; nothing when the dealer did not say. */
export const dailyKmLabel = (terms: RentalCarTerms): string | undefined => {
  if (terms.dailyKmUnlimited === true) return 'Unlimited km';

  return typeof terms.dailyKmLimit === 'number' && terms.dailyKmLimit > 0
    ? `${formatAmount(terms.dailyKmLimit)} km/day`
    : undefined;
};

/** The card's words: the parts the dealer gave, joined — or nothing at all. */
export const rentalCarTermsLine = (terms: RentalCarTerms): string | undefined => {
  const parts = [seatsLabel(terms.seats), dailyKmLabel(terms)].filter((part): part is string => !!part);

  return parts.length > 0 ? parts.join(' · ') : undefined;
};

export interface RentalCarTermRow {
  label: 'Seats' | 'Daily limit';
  value: string;
}

/** The rows of the listing screen, in a fixed order — only those the dealer filled in. */
export const rentalCarTermRows = (terms: RentalCarTerms): RentalCarTermRow[] => {
  const limit =
    terms.dailyKmUnlimited === true
      ? 'Unlimited'
      : typeof terms.dailyKmLimit === 'number' && terms.dailyKmLimit > 0
        ? `${formatAmount(terms.dailyKmLimit)} km`
        : undefined;
  const rows: [RentalCarTermRow['label'], string | undefined][] = [
    ['Seats', typeof terms.seats === 'number' && terms.seats > 0 ? String(terms.seats) : undefined],
    ['Daily limit', limit],
  ];

  return rows.flatMap(([label, value]) => (value ? [{ label, value }] : []));
};
